/* eslint-disable react/forbid-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18next from 'i18next';
import styled from 'styled-components';

import CommentInput from '../../components/CommentInput/CommentInput';
import {
  clearEditCommentError,
  clearPostCommentError,
  clearPostReplyError,
  editChildRequest,
  editParentRequest,
  postCommentRequest,
  postReplyRequest,
  toggleCommentEditing
} from '../../store/actions/comments/commentsActions';
import './CommentInputContainer.css';
import CallToActionWrapper from '../../components/CallToActionWrapper/CallToActionWrapper';
import {
  getEditingStatusProps,
  getParentAuthor,
  getTranslationStatusProps,
  hasPostingError,
  hasReplyPostingError
} from '../../store/selectors/comments/commentsSelectors';

import {
  generateSearchParameters,
  requestMentionableUsers
} from '../../utils/mentioning/requestMentionableUsers';
import { formatMentioningResults } from '../../utils/mentioning/mentionFormatters';
import {
  getCommentsContext,
  getSocialFeedCommunityId,
  getSocialFeedShowInput
} from '../../store/selectors/settings/settingsSelectors';
import { COMMENTS_CONTEXT_SOCIAL_FEED } from '../../utils/constants/commentConstants';
import CommunitySelectModal from '../../components/CommunitySelectModal/CommunitySelectModal';
import {
  setCommunityFilterType,
  setCommunityPostingId
} from '../../store/actions/mentioning/mentioningActions';
import {
  getCommunityFilter,
  getCommunityPostingId,
  getCommunityType,
  getAllCommunities
} from '../../store/selectors/community/communitySelectors';
import {
  getCurrentUserCommunities,
  getUserCommunitiesRequest
} from '../../store/selectors/user/userSelectors';
import { isShowMentionsModal } from '../../store/selectors/ui/uiSelectors';
import { toggleMentionModal } from '../../store/actions/ui/uiActions';
import { ALL_COMMUNITIES_CHOICE } from '../../utils/community/communityConstants';
import {
  generateVisibleCommunites,
  hasVisibleCommunity
} from '../../utils/community/communityUtils';

// Wraps the input container in a div and gives it specific z-index
// This z-index is dynamic to always keep the mention list above other components
// including other CommentInputContainers.
const StyledCommentInputContainer = styled.div`
  z-index: ${({ zIndex }) => zIndex};
`;

const StyledSpacedContainer = styled.div`
  margin-top: 20px;
`;

const showCommunitySelector = (
  userCommunityRequest,
  userCommunities,
  contentCommunities,
  commentsContext,
  isMultipleCommunityPosting,
  isVisibleCommunitySelectModal,
  communities,
  communityPostId
) => {
  let showSelector = false;
  const {
    isFetching: isFetchingUserCommunities,
    success: successUserCommunities
  } = userCommunityRequest;
  // social feed has no community selector
  if (commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED) {
    return showSelector;
  }
  if (!isMultipleCommunityPosting) {
    return showSelector;
  }
  if (isVisibleCommunitySelectModal) {
    return showSelector;
  }
  if (isFetchingUserCommunities || !successUserCommunities) {
    showSelector = communities && !communityPostId;
  } else {
    showSelector =
      Object.keys(
        generateVisibleCommunites(contentCommunities, userCommunities)
      ).length > 1 && !communityPostId;
  }
  return showSelector;
};

export class CommentInputContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inputFocus: false,
      posting: false,
      feedShowInput: false
    };

    this.mentioningRequestParameters = null;
  }

  // This lifecycle method is being used to wipe the input value when a successful post occurs.
  // Access to a previous version of the prop is accomplished by mirroring the prop in the state.
  static getDerivedStateFromProps(nextProps, prevState) {
    // quick escape if the isPosting redux post hasn't changed.
    if (nextProps.isPosting === prevState.posting) {
      return null;
    }
    // check for change in isPosting prop from false > true
    if (
      nextProps.isPosting === false &&
      prevState.posting === true &&
      nextProps.error === false
    ) {
      nextProps.setInputValue('');
      return {
        ...prevState,
        posting: nextProps.isPosting
      };
    }
    return {
      ...prevState,
      posting: nextProps.isPosting
    };
  }

  // This lifecycle method is being used to setup an event listener to allow for communication from the DesignStudio
  componentDidMount() {
    const { contentId } = this.props;
    const setToggleInput = () => {
      const { feedShowInput } = this.state;
      this.setState({ feedShowInput: !feedShowInput });
    };
    window.addEventListener(
      'DesignStudioToggleComment',
      function(e) {
        const eventId = e.detail && e.detail.contentId;
        if (eventId && eventId === contentId) {
          setToggleInput();
        }
      },
      false
    );
  }

  componentDidUpdate(prevProps) {
    const {
      communitySelectedData: proposedCommunityFilterData,
      setCommunityFilterType: setCommunityFilter,
      setCommunityPostingId: setCommunityPosting,
      toggleMentionModal: setMentionModal,
      currentCommunityFilterData
    } = this.props;
    // Checks if the proposed community to switch to is different to
    // the previous and currently selected community. This is to avoid the
    // lost mentions modal appearing multiple times when switching between communities
    // after selecting "cancel" when propted.

    if (
      proposedCommunityFilterData &&
      proposedCommunityFilterData?.id !==
        prevProps?.communitySelectedData?.id &&
      proposedCommunityFilterData?.id !== currentCommunityFilterData?.id
    ) {
      const { inputValue } = this.props;
      const inputHasLength = Boolean(inputValue && inputValue.length > 0);
      if (inputHasLength) {
        setMentionModal(true);
      } else {
        if (proposedCommunityFilterData.id === ALL_COMMUNITIES_CHOICE.id) {
          setCommunityPosting(undefined);
        } else {
          setCommunityPosting(proposedCommunityFilterData.id);
        }
        setCommunityFilter(proposedCommunityFilterData);
      }
    }
  }

  handleRequestMentionableUsers = async (query, cb) => {
    const {
      communityId,
      communityType,
      commentsContext,
      isMultipleCommunityPosting,
      communityPostId,
      parentId
    } = this.props;
    // Disables mentions by return an empty array if we are on the social feed widget
    if (commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED) return cb([]);

    // Because the library uses a callback,
    // it's simpler to just request results
    // from the API directly
    // rather than threading them through ReduxF
    let selectedCommunityId = communityId;

    if (!parentId && isMultipleCommunityPosting && communityPostId) {
      selectedCommunityId = communityPostId;
    }

    // Memoise the static parts of the
    // query so they don't need to be regenrated
    // on each keystroke
    this.mentioningRequestParameters = generateSearchParameters({
      communityId: selectedCommunityId,
      communityType,
      isMultipleCommunityPosting
    });

    const res = await requestMentionableUsers({
      ...this.mentioningRequestParameters,
      query
    });

    if (Array.isArray(res.items) && res.items.length > 0) {
      const formattedResults = formatMentioningResults(res.items);
      return cb(formattedResults);
    }

    return cb([]);
  };

  handleSelectPostCommunity = (communityKey) => {
    const {
      setCommunityFilterType: setCommunityFilter,
      setCommunityPostingId: setCommunityPostingComment,
      communities,
      handleCommunitySelected
    } = this.props;
    setCommunityPostingComment(communityKey);
    if (communityKey) {
      setCommunityFilter(communities[communityKey]);
      handleCommunitySelected();
    }
  };

  handleTextChange = (evnt) => {
    const {
      communityPostId,
      isMultipleCommunityPosting,
      commentsContext,
      setInputValue
    } = this.props;
    // If we have NOT yet selected a community and we are on a
    // piece of content that sits in multiple communities then
    // prevent the users from entering text and show the modal
    // to select a community
    if (
      commentsContext !== COMMENTS_CONTEXT_SOCIAL_FEED &&
      !communityPostId &&
      isMultipleCommunityPosting
    ) {
      this.handleCommunityModalFocus();
      setInputValue('');
    } else {
      const { value } = evnt.target;
      const updatedValue = !value ? '' : value;
      setInputValue(updatedValue);
    }
  };

  handleCancelComment = () => {
    const { isEditing, setInputValue } = this.props;
    setInputValue('');
    return isEditing ? this.handleCancelEditing() : this.handleCancelPosting();
  };

  handleCancelPosting = () => {
    const { parentId, hideInputCallback, error } = this.props;
    if (error) {
      this.clearError();
    }
    if (parentId) {
      return hideInputCallback();
    }
    return undefined; // Avoids ESLint rule *shrug*
  };

  handleCancelEditing = () => {
    const {
      commentId,
      parentId,
      toggleCommentEditing: toggleEditing,
      error,
      clearEditCommentError: dispatchClearEditCommentError
    } = this.props;
    if (error) {
      dispatchClearEditCommentError(commentId, parentId);
    }
    return toggleEditing({ commentId, rootId: parentId });
  };

  handleInputFocusChange = (focus) => {
    const { inputFocus } = this.state;
    if (inputFocus !== focus) {
      if (focus) {
        this.handleCommunityModalFocus();
      }
      this.setState({ inputFocus: focus });
    }
  };

  handleCommunityModalFocus = () => {
    const {
      toggleShowCommunitySelectModal,
      communities,
      isVisibleCommunitySelectModal,
      communityPostId,
      isMultipleCommunityPosting,
      commentsContext,
      userCommunitiesRequest,
      userCommunities,
      contentCommunities
    } = this.props;
    if (
      showCommunitySelector(
        userCommunitiesRequest,
        userCommunities,
        contentCommunities,
        commentsContext,
        isMultipleCommunityPosting,
        isVisibleCommunitySelectModal,
        communities,
        communityPostId
      )
    ) {
      toggleShowCommunitySelectModal(true);
    }
  };

  handleSubmitComment = () => {
    const { isEditing, inputValue } = this.props;
    return isEditing
      ? this.editComment(inputValue)
      : this.postComment(inputValue);
  };

  editComment = (commentText) => {
    const {
      editParentRequest: editParent,
      editChildRequest: editChild,
      contentId,
      parentId,
      commentId
    } = this.props;
    const editCommentArguments = {
      commentId,
      contentId,
      commentText,
      rootId: parentId
    };
    return parentId
      ? editChild(editCommentArguments)
      : editParent(editCommentArguments);
  };

  postComment = (commentText) => {
    const {
      postCommentRequest: postComment,
      postReplyRequest: postReply,
      contentId,
      communityId,
      parentId,
      rootId,
      commentsContext,
      socialFeedCommunityId,
      communityPostId,
      isMultipleCommunityPosting
    } = this.props;

    // comment community id by default is contentCommunity
    // if social feed selected, social Id used
    // if user selects from comment modal an id
    // we use that id for comment
    let selectedCommunityId = communityId;
    if (
      commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED &&
      socialFeedCommunityId
    ) {
      selectedCommunityId = socialFeedCommunityId;
    } else if (isMultipleCommunityPosting && communityPostId) {
      selectedCommunityId = communityPostId;
    }

    const postCommentArguments = {
      contentId,
      commentText,
      communityId: selectedCommunityId,
      parentId,
      rootId: !rootId ? parentId : rootId
    };
    if (parentId) {
      postReply(postCommentArguments);
    } else {
      postComment(postCommentArguments);
    }
  };

  clearError = () => {
    const {
      clearPostCommentError: clearCommentError,
      clearPostReplyError: clearReplyError,
      rootId,
      parentId
    } = this.props;
    if (parentId) {
      const commentRoot = rootId || parentId;
      return clearReplyError(commentRoot);
    }
    return clearCommentError();
  };

  render() {
    const {
      avatar,
      userId,
      parentId,
      isEditing,
      isPosting,
      error,
      communityType,
      zIndex,
      parentAuthor,
      commentsContext,
      socialFeedShowInput,
      toggleShowCommunitySelectModal,
      isVisibleCommunitySelectModal,
      communityPostId,
      isMultipleCommunityPosting,
      inputValue,
      userCommunitiesRequest,
      userCommunities,
      contentCommunities
    } = this.props;
    const { inputFocus, feedShowInput } = this.state;
    const {
      isFetching: isFetchingUserCommunities,
      success: successUserCommunities
    } = userCommunitiesRequest;
    const placeholder = parentId
      ? {
          key: 'replyToAuthorInputPlaceholder',
          params: { author: parentAuthor }
        }
      : {
          key: 'commentInputPlaceholder',
          params: {}
        };
    const inputHasLength = Boolean(inputValue && inputValue.length > 0);
    let isPostingEnabled;
    if (isMultipleCommunityPosting) {
      // Ignore the communityPostId check since this is not applicable when
      // on the social feed widget
      if (commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED) {
        isPostingEnabled =
          !isPosting && inputHasLength && !isVisibleCommunitySelectModal;
      } else {
        isPostingEnabled =
          !isPosting &&
          inputHasLength &&
          !isVisibleCommunitySelectModal &&
          !!communityPostId;
      }
    } else {
      // if the community is protected/private we need to wait for the user communities to return.
      const visibleCommunity = hasVisibleCommunity(
        contentCommunities,
        successUserCommunities ? userCommunities : {}
      );
      if (visibleCommunity) {
        isPostingEnabled = !isPosting && inputHasLength;
      } else {
        isPostingEnabled = false;
      }
    }
    const renderInputContainer = () => {
      return (
        <StyledCommentInputContainer
          styleName={`wrapper ${parentId ? 'wrapper-reply' : ''} ${
            isEditing ? 'wrapper-editing' : ''
          }`}
          zIndex={zIndex}
        >
          <CommentInput
            userId={userId}
            avatar={isEditing ? '' : avatar}
            placeHolder={
              i18next.t(placeholder.key, placeholder.params) ||
              placeholder.default
            }
            onTextChange={this.handleTextChange}
            onFocusChange={this.handleInputFocusChange}
            commentText={inputValue || ''}
            ref={this.inputComponent}
            hasError={error}
            handleRequestMentionableUsers={this.handleRequestMentionableUsers}
            communityType={communityType}
          />
          <div styleName={error ? 'cta-container-error' : 'cta-container'}>
            {error && <p styleName="error-text">{i18next.t('postingError')}</p>}
            <CallToActionWrapper
              isVisible={
                inputHasLength || inputFocus || !!parentId || isEditing
              }
              isPostingEnabled={isPostingEnabled}
              handleSubmitComment={this.handleSubmitComment}
              handleCancelComment={this.handleCancelComment}
            />
          </div>
          {isVisibleCommunitySelectModal &&
            commentsContext !== COMMENTS_CONTEXT_SOCIAL_FEED && (
              <CommunitySelectModal
                communities={generateVisibleCommunites(
                  contentCommunities,
                  userCommunities
                )}
                onCommunitySelect={this.handleSelectPostCommunity}
                onClose={toggleShowCommunitySelectModal}
                communitiesLoading={isFetchingUserCommunities}
              />
            )}
        </StyledCommentInputContainer>
      );
    };
    return (
      <>
        {socialFeedShowInput !== true &&
        commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED &&
        !parentId &&
        !feedShowInput ? (
          <StyledSpacedContainer />
        ) : (
          renderInputContainer()
        )}
      </>
    );
  }
}

CommentInputContainer.propTypes = {
  contentId: PropTypes.number.isRequired,
  communityId: PropTypes.number,
  avatar: PropTypes.string,
  userId: PropTypes.number,
  postCommentRequest: PropTypes.func.isRequired,
  postReplyRequest: PropTypes.func.isRequired,
  parentId: PropTypes.number,
  rootId: PropTypes.number,
  hideInputCallback: PropTypes.func,
  isEditing: PropTypes.bool,
  isPosting: PropTypes.bool,
  commentId: PropTypes.number,
  editParentRequest: PropTypes.func.isRequired,
  editChildRequest: PropTypes.func.isRequired,
  toggleCommentEditing: PropTypes.func.isRequired,
  error: PropTypes.bool,
  clearPostCommentError: PropTypes.func.isRequired,
  clearPostReplyError: PropTypes.func.isRequired,
  clearEditCommentError: PropTypes.func.isRequired,
  communityType: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  parentAuthor: PropTypes.string,
  commentsContext: PropTypes.string,
  socialFeedCommunityId: PropTypes.number,
  socialFeedShowInput: PropTypes.bool,
  toggleShowCommunitySelectModal: PropTypes.func.isRequired,
  isVisibleCommunitySelectModal: PropTypes.bool,
  communities: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }).isRequired,
  setCommunityFilterType: PropTypes.func.isRequired,
  setCommunityPostingId: PropTypes.func.isRequired,
  communityPostId: PropTypes.number,
  isMultipleCommunityPosting: PropTypes.bool.isRequired,
  toggleMentionModal: PropTypes.func.isRequired,
  communitySelectedData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }),
  currentCommunityFilterData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }),
  handleCommunitySelected: PropTypes.func.isRequired,
  inputValue: PropTypes.string,
  setInputValue: PropTypes.func,
  userCommunitiesRequest: PropTypes.shape({
    success: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    isFetching: PropTypes.bool
  }),
  contentCommunities: PropTypes.object,
  userCommunities: PropTypes.object
};

CommentInputContainer.defaultProps = {
  communityId: null,
  avatar: null,
  parentId: null,
  rootId: null,
  hideInputCallback: () => {},
  isEditing: false,
  userId: undefined,
  commentId: null,
  error: false,
  communityType: null,
  zIndex: 'auto',
  isPosting: false,
  parentAuthor: null,
  commentsContext: null,
  socialFeedCommunityId: null,
  socialFeedShowInput: null,
  isVisibleCommunitySelectModal: false,
  communityPostId: undefined,
  communitySelectedData: {},
  currentCommunityFilterData: {},
  inputValue: '',
  setInputValue: () => {},
  userCommunitiesRequest: {},
  userCommunities: {},
  contentCommunities: []
};

const mapStateToProps = (state, ownProps) => {
  const {
    isEditing,
    commentId,
    parentId = null,
    rootId,
    communityId,
    inputValue,
    initialText,
    edited
  } = ownProps;
  const editing = isEditing
    ? getEditingStatusProps(state, commentId, parentId)
    : {};
  const translation = isEditing
    ? getTranslationStatusProps(state, commentId, parentId)
    : {};
  const parentAuthor = parentId ? getParentAuthor(state, parentId) : null;
  let isPosting;
  let error;
  let previousComment = null;
  let isTranslated = false;

  if (isEditing) {
    ({ error, submitting: isPosting } = editing);
    ({ previousComment, isTranslated } = translation);
  } else {
    ({ isPosting } = state.commenting);
    const ancestor = rootId || parentId;
    error = ancestor
      ? hasReplyPostingError(state, ancestor)
      : hasPostingError(state);
  }
  // get both avatars and decide which to use
  const { avatarUrl = null, bigAvatarUrl = null, id: userId } = state.user;
  const avatar = bigAvatarUrl || avatarUrl;

  const communityType = getCommunityType(state, communityId);

  const getInitialText = () => {
    if (edited) {
      return inputValue;
    }
    if (isEditing && previousComment && isTranslated) {
      return previousComment;
    }
    return initialText || inputValue;
  };

  return {
    isPosting,
    error,
    avatar,
    userId,
    communityType,
    previousComment,
    isTranslated,
    parentAuthor,
    commentsContext: getCommentsContext(state),
    socialFeedCommunityId: getSocialFeedCommunityId(state),
    socialFeedShowInput: getSocialFeedShowInput(state),
    communityPostId: getCommunityPostingId(state),
    isShowMentionsModal: isShowMentionsModal(state),
    currentCommunityFilterData: getCommunityFilter(state),
    inputValue: getInitialText(),
    userCommunities: getCurrentUserCommunities(state),
    userCommunitiesRequest: getUserCommunitiesRequest(state),
    contentCommunities: getAllCommunities(state)
  };
};

const mapDispatchToProps = {
  postCommentRequest,
  postReplyRequest,
  editParentRequest,
  editChildRequest,
  toggleCommentEditing,
  clearPostCommentError,
  clearPostReplyError,
  clearEditCommentError,
  setCommunityFilterType,
  setCommunityPostingId,
  toggleMentionModal
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CommentInputContainer);
